import { useMemo } from 'react';

import {
    AccommodationClustersInput,
    ClusterOrMarkerType,
    ClusterType,
    MarkerDataType,
    MarkerTypeEnum,
} from '@app/models/map-marker.model';

import Supercluster from 'supercluster';

interface ClusterProperties {
    cluster: true;
    point_count: number;
    cluster_id: number;
}

const CLUSTERING_CONFIG = {
    zoomLevels: [
        { minZoom: 17, radius: 30 },
        { minZoom: 15, radius: 40 },
        { minZoom: 14, radius: 200 },
        { minZoom: 13, radius: 250 },
    ],
    defaultRadius: 330,
    maxZoom: 18,
    minPoints: 7,
};

export const useMapAccommodationClusters = ({
    points,
    bounds,
    zoom,
}: AccommodationClustersInput): ClusterOrMarkerType[] | undefined => {
    const getClusterRadiusForZoom = (zoom: number | null): number => {
        if (!zoom) return CLUSTERING_CONFIG.defaultRadius;

        for (const { minZoom, radius } of CLUSTERING_CONFIG.zoomLevels) {
            if (zoom >= minZoom) {
                return radius;
            }
        }

        return CLUSTERING_CONFIG.defaultRadius;
    };

    const supercluster = useMemo(() => {
        if (!points) {
            return undefined;
        }
        const radius = getClusterRadiusForZoom(zoom);
        const cluster = new Supercluster<MarkerDataType, ClusterProperties>({
            radius,
            maxZoom: CLUSTERING_CONFIG.maxZoom,
            minPoints: CLUSTERING_CONFIG.minPoints,
        });

        const features = points.map(p => ({
            type: 'Feature' as const,
            geometry: {
                type: 'Point' as const,
                coordinates: [p.position.lng, p.position.lat],
            },
            properties: {
                ...p,
            },
        }));

        cluster.load(features);
        return cluster;
    }, [points, zoom]);

    const clusters = useMemo(() => {
        if (!points || !bounds || !supercluster) {
            return undefined;
        }

        const rawClusters = supercluster.getClusters(
            [bounds.west, bounds.south, bounds.east, bounds.north],
            Math.round(zoom),
        );

        return rawClusters.map(feature => {
            const props = feature.properties as ClusterProperties & MarkerDataType;

            if ('cluster' in props && props.cluster) {
                const clusterId = props.cluster_id;

                const leaves = supercluster.getLeaves(clusterId, Infinity);

                const markers: MarkerDataType[] = leaves.map(leaf => {
                    const props = leaf.properties;
                    return {
                        ...props,
                        isCluster: false,
                    };
                });

                const cluster: ClusterType = {
                    id: `cluster-${clusterId}`,
                    isCluster: true,
                    type: MarkerTypeEnum.Cluster,
                    count: props.point_count,
                    position: {
                        lat: feature.geometry.coordinates[1],
                        lng: feature.geometry.coordinates[0],
                    },
                    points: markers,
                };

                return cluster;
            }

            const marker: MarkerDataType = {
                ...(feature.properties as MarkerDataType),
                isCluster: false,
            };

            return marker;
        });
    }, [supercluster, bounds, points, zoom]);

    return clusters ?? undefined;
};
